import type { CatalogItem } from '@/lib/api/types.js';
import { cn } from '@/lib/utils.js';
import { Link } from '@tanstack/react-router';

interface CatalogListItemProps {
  item: CatalogItem;
  onFavoriteToggle: (tenantReportId: string, currentValue: boolean) => void;
  isPending?: boolean;
}

function CatalogListItem({ item, onFavoriteToggle, isPending = false }: CatalogListItemProps) {
  const titleEs = item.title.es ?? item.code;

  return (
    <div className="flex items-center gap-3 border-b border-[var(--color-qup-border-dark)] py-3 last:border-b-0">
      {/* Favorite */}
      <button
        type="button"
        aria-pressed={item.is_favorite ? 'true' : 'false'}
        aria-label={item.is_favorite ? 'Quitar de favoritos' : 'Añadir a favoritos'}
        disabled={isPending}
        onClick={() => onFavoriteToggle(item.tenant_report_id, item.is_favorite)}
        className="flex-shrink-0 text-[var(--color-qup-text-muted-dark)] transition-colors hover:text-[var(--color-qup-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)] rounded disabled:opacity-50"
      >
        {item.is_favorite ? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="var(--color-qup-primary)"
            stroke="var(--color-qup-primary)"
            strokeWidth="2"
            aria-hidden="true"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ) : (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        )}
      </button>

      {/* Title + link */}
      <Link
        to="/reports/$id"
        params={{ id: item.tenant_report_id }}
        className={cn(
          'min-w-0 flex-1 text-sm font-medium text-[var(--color-qup-text-dark)]',
          'hover:text-[var(--color-qup-primary)] transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)] rounded',
        )}
      >
        <span className="block truncate">{titleEs}</span>
      </Link>

      {/* Category + kind */}
      <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
        <span className="rounded-full bg-[var(--color-qup-border-dark)]/60 px-2 py-0.5 text-[11px] text-[var(--color-qup-text-muted-dark)]">
          {item.category}
        </span>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[11px] font-medium',
            item.kind === 'report'
              ? 'bg-[var(--color-qup-info)]/15 text-[var(--color-qup-info)]'
              : 'bg-[var(--color-qup-warning)]/15 text-[var(--color-qup-warning)]',
          )}
        >
          {item.kind === 'report' ? 'Informe' : 'Dashboard'}
        </span>
      </div>
    </div>
  );
}

interface CatalogListProps {
  items: ReadonlyArray<CatalogItem>;
  onFavoriteToggle: (tenantReportId: string, currentValue: boolean) => void;
  pendingIds?: ReadonlySet<string>;
}

export function CatalogList({ items, onFavoriteToggle, pendingIds }: CatalogListProps) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)] bg-[var(--color-qup-bg-card-dark)] px-4 shadow-[var(--shadow-card)]">
      {items.map((item) => (
        <CatalogListItem
          key={item.tenant_report_id}
          item={item}
          onFavoriteToggle={onFavoriteToggle}
          isPending={pendingIds?.has(item.tenant_report_id) ?? false}
        />
      ))}
    </div>
  );
}
